import { DependencyList, useEffect, useState } from 'react';
import { isAbortError } from './api';

export interface AgentResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// Collapses the loading/error/data triplet + AbortController dance that every
// agent-detail fetch repeats. The fetcher receives the AbortController so it can
// pass the signal to `request.get`; the hook aborts on deps-change/unmount and
// ignores any settle that lands after the abort. `enabled` gates the fetch — when
// false the hook stays idle (no fetch, no loading) and clears any prior data, so
// a side panel doesn't flash a stale resource. Aborts are swallowed (never
// surfaced as an error) via `isAbortError`.
export const useAgentResource = <T>(
  fetcher: (abortController: AbortController) => Promise<T>,
  deps: DependencyList,
  enabled = true
): AgentResource<T> => {
  const [data, setData] = useState<T | null>(null);
  // Start loading when a fetch is imminent (enabled at mount), so the first
  // render already shows the loading state instead of a one-frame empty flash.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Idle: drop any prior result so a re-enable starts clean.
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setData(null);
    fetcher(controller)
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch((e) => {
        if (controller.signal.aborted || isAbortError(e)) return;
        setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // The fetcher is recreated per render; callers list its real inputs in `deps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, loading, error };
};
